// client/src/components/indicadores/FaturamentoReport.js
import React, { useState, useEffect } from 'react';
import api from '../../services/api'; // ✅ USANDO API PADRONIZADA
import useAlert from '../../hooks/useAlert';
import { useAuth } from '../../context/auth/AuthProvider';
import { useFaturamentoReport } from '../../hooks/useReports';
import { useTenantI18n } from '../../hooks/useAccounts';
import { formatCurrency } from '../../utils/formatters';
import { DollarSign, TrendingUp, Users, CreditCard } from 'lucide-react';
import ReportPageLayout from './ReportPageLayout';
import ReportTable from './ReportTable';
import KpiCard from '../dashboard/KpiCard';

// ✅ DEFINIR FILTROS INICIAIS COMO CONSTANTE
const initialFilters = {
  startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  endDate: new Date(),
};

const FaturamentoReport = () => {
    const { setAlert } = useAlert();
    const { user, scope } = useAuth();
    // ✅ ACHADO: relatorio inteiro fixo em BRL (R$ + toLocaleString), sem
    // ler configuracoesGerais.i18n da conta.
    const { currency, locale } = useTenantI18n();

    // ✅ INICIAR COM FILTROS VÁLIDOS, NUNCA NULL
    const [appliedFilters, setAppliedFilters] = useState(initialFilters);
    const [filterOptions, setFilterOptions] = useState({
        unidades: [],
        profissionais: [],
        servicos: [],
        recursos: [],
    });
    const [loadingFilters, setLoadingFilters] = useState(false);

    // 🔥 USANDO NOVO HOOK - useFaturamentoReport
    const { 
        data: reportData = {}, // ✅ VALOR PADRÃO COMO OBJETO VAZIO
        isLoading,
        error 
    } = useFaturamentoReport(appliedFilters);

    // ✅ TRATAMENTO DE ERRO PADRONIZADO
    useEffect(() => {
        if (error) {
            console.error('❌ Erro no relatório de faturamento:', error);
            
            let errorMessage = 'Ocorreu um erro ao buscar o relatório de faturamento.';
            
            if (error.message.includes('Servidor indisponível')) {
                errorMessage = error.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            setAlert(errorMessage, 'danger');
        }
    }, [error, setAlert]);

    // 🔥 CARREGAR OPÇÕES DE FILTRO - PADRONIZADO
    useEffect(() => {
        const fetchFilterOptions = async () => {
            // ✅ CORREÇÃO: só bloqueava sem user.account — franquia_admin
            // (tenant em `.franquia`) e matriz_admin (sem tenant nenhum)
            // nunca viam essas opções de filtro, mesmo com o relatório em
            // si carregando normalmente. Os endpoints (/unidades,
            // /servicos, /recursos, /users) já escopam sozinhos pelo JWT
            // (filterByUserUnits) — não dependem do `account` enviado aqui.
            if (!user?.account?._id && !user?.franquia?._id && scope !== 'matriz') {
                console.log('⚠️ Usuário sem conta, franquia ou escopo matriz — sem opções de filtro');
                return;
            }

            setLoadingFilters(true);

            try {
                const accountId = user?.account?._id;
                const baseParams = accountId ? { account: accountId, limit: 1000 } : { limit: 1000 };
                console.log('🔄 Carregando filtros de faturamento', baseParams);

                // ✅ PADRONIZADO: Usando a mesma estrutura dos outros componentes
                const requests = [
                    // Unidades
                    api.get('/unidades', { params: baseParams }),
                    // Profissionais
                    api.get('/users', { params: { ...baseParams, role: 'profissional' } }),
                    // Serviços
                    api.get('/servicos', { params: baseParams }),
                    // Recursos
                    api.get('/recursos', { params: baseParams })
                ];

                console.log('📡 Fazendo requisições para filtros...');
                
                const [unidadesRes, profissionaisRes, servicosRes, recursosRes] = await Promise.all(requests);

                // ✅ PADRONIZADO: Mesma estrutura de extração de dados
                const filterData = {
                    unidades: unidadesRes.data?.data || unidadesRes.data || [],
                    profissionais: profissionaisRes.data?.data || profissionaisRes.data || [],
                    servicos: servicosRes.data?.data || servicosRes.data || [],
                    recursos: recursosRes.data?.data || recursosRes.data || []
                };

                setFilterOptions(filterData);

                console.log('✅ Filtros de faturamento carregados com sucesso:', {
                    unidades: filterData.unidades.length,
                    profissionais: filterData.profissionais.length,
                    servicos: filterData.servicos.length,
                    recursos: filterData.recursos.length
                });

            } catch (error) {
                console.error('❌ Erro ao carregar filtros de faturamento:', error);
                
                let errorMessage = 'Erro ao carregar opções de filtro.';
                
                if (error.message.includes('Servidor indisponível')) {
                    errorMessage = 'Servidor backend indisponível. Verifique se está rodando.';
                } else if (error.response?.data?.error) {
                    errorMessage = error.response.data.error;
                } else if (error.message) {
                    errorMessage = error.message;
                }
                
                setAlert(errorMessage, 'danger');
                
                // ✅ FALLBACK: Definir arrays vazios para evitar quebra da UI
                setFilterOptions({
                    unidades: [],
                    profissionais: [],
                    servicos: [],
                    recursos: []
                });
            } finally {
                setLoadingFilters(false);
            }
        };
        
        fetchFilterOptions();
    }, [user, scope, setAlert]);

    // ✅ FUNÇÃO PARA APLICAR FILTROS (GARANTINDO QUE NÃO SEJA NULL)
    const handleApplyFilters = (filters) => {
        console.log('🔄 Aplicando filtros:', filters);
        setAppliedFilters(filters || initialFilters);
    };

    // 🔥 KPIs DINÂMICOS COM VALORES PADRÃO SEGUROS (AGORA BASEADO EM VENDAS)
    const kpiCards = [
        {
            title: "Faturamento Total",
            value: formatCurrency(reportData?.totalGeral?.[0]?.total || 0, currency, locale),
            prefix: "",
            icon: DollarSign,
            description: "Total faturado em vendas"
        },
        { 
            title: "Total de Vendas", 
            value: (reportData?.totalGeral?.[0]?.count || 0).toLocaleString('pt-BR'), 
            prefix: "",
            icon: TrendingUp,
            description: "Quantidade de vendas realizadas"
        },
        { 
            title: "Profissionais", 
            value: (reportData?.porProfissional?.length || 0).toLocaleString('pt-BR'), 
            prefix: "",
            icon: Users,
            description: "Profissionais com vendas"
        },
        { 
            title: "Produtos/Serviços", 
            value: (reportData?.porServico?.length || 0).toLocaleString('pt-BR'), 
            prefix: "",
            icon: CreditCard,
            description: "Itens diferentes vendidos"
        }
    ];

    // 🔥 COLUNAS DAS TABELAS - AJUSTADAS PARA VENDAS
    const professionalColumns = [
        { 
            key: 'nome', 
            header: 'Profissional', 
            render: (row) => row.nome || 'Não informado',
            sortable: true
        },
        { 
            key: 'count', 
            header: 'Vendas Realizadas', 
            render: (row) => row.count || 0,
            sortable: true
        },
        { 
            key: 'total', 
            header: 'Faturamento', 
            render: (row) => formatCurrency(row.total || 0, currency, locale),
            sortable: true
        },
        {
            key: 'ticketMedio',
            header: 'Ticket Médio',
            render: (row) => formatCurrency(row.ticketMedio || 0, currency, locale),
            sortable: true
        },
        {
            key: 'totalComissoes',
            header: 'Comissões',
            render: (row) => formatCurrency(row.totalComissoes || 0, currency, locale),
            sortable: true
        }
    ];
    
    const serviceColumns = [
        { 
            key: 'nome', 
            header: 'Produto/Serviço', 
            render: (row) => row.nome || 'Item não informado',
            sortable: true
        },
        { 
            key: 'tipo', 
            header: 'Tipo', 
            render: (row) => {
                const tipos = {
                    'Servico': 'Serviço',
                    'ItemVenda': 'Produto',
                    'Recurso': 'Recurso',
                    'PacoteCliente': 'Pacote',
                    'Curso': 'Curso'
                };
                return tipos[row.tipo] || row.tipo || 'Não informado';
            },
            sortable: true
        },
        { 
            key: 'count', 
            header: 'Quantidade', 
            render: (row) => (row.count || 0).toLocaleString('pt-BR'),
            sortable: true
        },
        { 
            key: 'total', 
            header: 'Faturamento', 
            render: (row) => formatCurrency(row.total || 0, currency, locale),
            sortable: true
        },
        {
            key: 'valorMedio',
            header: 'Valor Médio',
            render: (row) => formatCurrency(row.valorMedio || 0, currency, locale),
            sortable: true
        },
        { 
            key: 'numeroVendas', 
            header: 'Vendas', 
            render: (row) => (row.numeroVendas || 0).toLocaleString('pt-BR'),
            sortable: true
        }
    ];

    // 🔥 NOVA TABELA: EVOLUÇÃO MENSAL
    const evolucaoColumns = [
        { 
            key: 'periodo', 
            header: 'Período', 
            render: (row) => row.periodo,
            sortable: true
        },
        { 
            key: 'total', 
            header: 'Faturamento', 
            render: (row) => formatCurrency(row.total || 0, currency, locale),
            sortable: true
        },
        {
            key: 'count',
            header: 'Vendas',
            render: (row) => (row.count || 0).toLocaleString('pt-BR'),
            sortable: true
        },
        {
            key: 'ticketMedio',
            header: 'Ticket Médio',
            render: (row) => formatCurrency(row.ticketMedio || 0, currency, locale),
            sortable: true
        }
    ];

    // 🔥 ESTATÍSTICAS ADICIONAIS - AGORA BASEADO EM VENDAS
    const getAdditionalStats = () => {
        if (!reportData || Object.keys(reportData).length === 0) return null;

        const totalGeral = reportData.totalGeral?.[0] || {};
        const porProfissional = reportData.porProfissional || [];
        const porServico = reportData.porServico || [];
        const evolucaoMensal = reportData.evolucaoMensal || [];

        const faturamentoTotal = totalGeral.total || 0;
        const totalVendas = totalGeral.count || 0;
        const totalDescontos = totalGeral.totalDescontos || 0;
        const subtotal = totalGeral.subtotal || 0;
        const ticketMedioGeral = totalVendas > 0 ? faturamentoTotal / totalVendas : 0;

        // Top profissional
        const topProfissional = porProfissional.length > 0 
            ? porProfissional.reduce((prev, current) => (prev.total > current.total) ? prev : current)
            : null;

        // Top produto/serviço
        const topItem = porServico.length > 0 
            ? porServico.reduce((prev, current) => (prev.total > current.total) ? prev : current)
            : null;

        // Crescimento mensal (se houver dados suficientes)
        let crescimento = 0;
        if (evolucaoMensal.length > 1) {
            const primeiroMes = evolucaoMensal[0]?.total || 0;
            const ultimoMes = evolucaoMensal[evolucaoMensal.length - 1]?.total || 0;
            crescimento = primeiroMes > 0 ? ((ultimoMes - primeiroMes) / primeiroMes) * 100 : 0;
        }

        return {
            faturamentoTotal,
            totalVendas,
            totalDescontos,
            subtotal,
            ticketMedioGeral,
            topProfissional,
            topItem,
            totalProfissionais: porProfissional.length,
            totalItens: porServico.length,
            crescimento: parseFloat(crescimento.toFixed(2)),
            periodoMaisVendas: evolucaoMensal.length > 0 
                ? evolucaoMensal.reduce((prev, current) => (prev.count > current.count) ? prev : current)?.periodo
                : null
        };
    };

    const stats = getAdditionalStats();

    // ✅ VERIFICAÇÃO DE CONEXÃO COM BACKEND
    const isBackendUnavailable = error?.message?.includes('Servidor indisponível');

    // ✅ CONFIGURAÇÕES DAS TABELAS
    const tableConfigurations = [
        {
            title: "Faturamento por Profissional",
            columns: professionalColumns,
            data: reportData?.porProfissional || [],
            defaultSort: 'total',
            defaultSortDirection: 'desc'
        },
        {
            title: "Faturamento por Produto/Serviço",
            columns: serviceColumns,
            data: reportData?.porServico || [],
            defaultSort: 'total',
            defaultSortDirection: 'desc'
        },
        {
            title: "Evolução Mensal do Faturamento",
            columns: evolucaoColumns,
            data: reportData?.evolucaoMensal || [],
            defaultSort: 'periodo',
            defaultSortDirection: 'asc'
        }
    ];

    return (
        <ReportPageLayout
            icon={<DollarSign size={24} />}
            title="Relatório de Faturamento - Vendas"
            onApplyFilters={handleApplyFilters}
            filterOptions={filterOptions}
            enabledFilters={['date', 'profissional', 'unidade', 'servico']}
            isLoading={isLoading || loadingFilters}
            kpiCards={kpiCards}
            tableConfigurations={tableConfigurations}
            initialFilters={initialFilters}
        >
            {/* 🔥 MENSAGEM DE ERRO DE CONEXÃO - PRIORIDADE MÁXIMA */}
            {isBackendUnavailable && (
                <div className="content-panel mb-4">
                    <div className="alert alert-danger">
                        <div className="d-flex align-items-center">
                            <div className="flex-grow-1">
                                <h5>❌ Erro de Conexão com o Servidor</h5>
                                <p className="mb-2">{error.message}</p>
                                <small className="text-muted">
                                    <strong>Para resolver:</strong><br/>
                                    1. Verifique se o servidor backend está rodando<br/>
                                    2. Confirme se a API está respondendo<br/>
                                    3. Verifique o comando: <code>npm run dev</code> no backend<br/>
                                    4. Recarregue a página após iniciar o servidor
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 🔥 TABELAS DE DETALHE — tableConfigurations era montado mas
                nunca renderizado (ReportPageLayout não usa essa prop, só
                `children`), então nenhuma das 3 tabelas aparecia na tela. */}
            {!isBackendUnavailable && tableConfigurations.map((config) => (
                <ReportTable
                    key={config.title}
                    title={config.title}
                    columns={config.columns}
                    data={config.data}
                    isLoading={isLoading}
                />
            ))}

            {/* 🔥 ESTATÍSTICAS DETALHADAS */}
            {!isBackendUnavailable && stats && (
                <div className="content-panel mb-4">
                    <h4 className="panel-title">Análise Detalhada do Período</h4>
                    <div className="row">
                        <div className="col-md-6">
                            <div className="stat-detail-card">
                                <h6>Performance Geral</h6>
                                <div className="stat-detail-item">
                                    <span>Ticket Médio:</span>
                                    <strong>{formatCurrency(stats.ticketMedioGeral, currency, locale)}</strong>
                                </div>
                                <div className="stat-detail-item">
                                    <span>Total de Descontos:</span>
                                    <strong>{formatCurrency(stats.totalDescontos, currency, locale)}</strong>
                                </div>
                                <div className="stat-detail-item">
                                    <span>Crescimento no Período:</span>
                                    <strong className={stats.crescimento >= 0 ? 'text-success' : 'text-danger'}>
                                        {stats.crescimento >= 0 ? '+' : ''}{stats.crescimento}%
                                    </strong>
                                </div>
                            </div>
                        </div>
                        
                        <div className="col-md-6">
                            <div className="stat-detail-card">
                                <h6>Destaques</h6>
                                {stats.topProfissional && (
                                    <div className="stat-detail-item">
                                        <span>Top Profissional:</span>
                                        <strong>{stats.topProfissional.nome} ({formatCurrency(stats.topProfissional.total, currency, locale)})</strong>
                                    </div>
                                )}
                                {stats.topItem && (
                                    <div className="stat-detail-item">
                                        <span>Item Mais Vendido:</span>
                                        <strong>{stats.topItem.nome} ({formatCurrency(stats.topItem.total, currency, locale)})</strong>
                                    </div>
                                )}
                                {stats.periodoMaisVendas && (
                                    <div className="stat-detail-item">
                                        <span>Mês com Mais Vendas:</span>
                                        <strong>{stats.periodoMaisVendas}</strong>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 🔥 RESUMO FINANCEIRO */}
            {!isBackendUnavailable && stats && (
                <div className="content-panel mt-4">
                    <h4 className="panel-title">Resumo Financeiro</h4>
                    <div className="dashboard-grid">
                        <KpiCard
                            icon={DollarSign}
                            title="Faturamento Total"
                            value={formatCurrency(stats.faturamentoTotal, currency, locale)}
                        />
                        <KpiCard
                            icon={TrendingUp}
                            title="Total de Vendas"
                            value={stats.totalVendas.toLocaleString('pt-BR')}
                        />
                        <KpiCard
                            icon={CreditCard}
                            title="Ticket Médio"
                            value={formatCurrency(stats.ticketMedioGeral, currency, locale)}
                        />
                        <KpiCard
                            icon={Users}
                            title="Profissionais Ativos"
                            value={stats.totalProfissionais.toLocaleString('pt-BR')}
                        />
                    </div>
                </div>
            )}

            {/* 🔥 ESTADO VAZIO MELHORADO */}
            {!isBackendUnavailable && !isLoading && (!reportData || Object.keys(reportData).length === 0) && (
                <div className="empty-state text-center py-5">
                    <DollarSign size={48} className="text-muted mb-3" />
                    <h5>Nenhum dado de faturamento encontrado</h5>
                    <p className="text-muted">
                        Não foram encontrados dados de faturamento para os filtros selecionados.
                        Verifique se há vendas no período selecionado.
                    </p>
                </div>
            )}
        </ReportPageLayout>
    );
};

export default FaturamentoReport;